import { useState } from "react";
import { styled } from "@pigment-css/react";
import { useTranslations, useLocale } from "@acto/web-common/locales";
import { SectionDivider, CollapsibleSection } from "../../../primitives";
import ClientTag from "@acto/taskpane/core/domain/ClientTag";

interface CustomerInfoSectionProps {
  purchaseFrequency: string;
  industry: string;
  lastVisit: string;
  lastOrder: string;
  clientTags: ClientTag[];
}

const InfoGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.managementView.spacing[32],
  columnGap: theme.managementView.spacing[16],
}));

const InfoItem = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[4],
}));

const Label = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[56],
}));

const Value = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[17],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[90],
}));

const TagsGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.managementView.spacing[32],
  columnGap: theme.managementView.spacing[16],
  paddingTop: theme.managementView.spacing[24],
}));

export const CustomerInfoSection = ({
  purchaseFrequency,
  industry,
  lastVisit,
  lastOrder,
  clientTags,
}: CustomerInfoSectionProps) => {
  const t = useTranslations();
  const { locale } = useLocale();
  const [isExpanded, setIsExpanded] = useState(false);

  const filteredTags = clientTags.filter((tag) => tag.name.hasTranslations());
  const hasExpandableContent = filteredTags.length > 0;

  return (
    <>
      <InfoGrid>
        <InfoItem>
          <Label>{t("managementView.customerDetails.purchaseFrequency")}</Label>
          <Value>{purchaseFrequency}</Value>
        </InfoItem>
        <InfoItem>
          <Label>{t("managementView.customerDetails.industry")}</Label>
          <Value>{industry}</Value>
        </InfoItem>
        <InfoItem>
          <Label>{t("managementView.customerDetails.lastVisit")}</Label>
          <Value>{lastVisit}</Value>
        </InfoItem>
        <InfoItem>
          <Label>{t("managementView.customerDetails.lastOrder")}</Label>
          <Value>{lastOrder}</Value>
        </InfoItem>
      </InfoGrid>

      {hasExpandableContent && (
        <CollapsibleSection isExpanded={isExpanded}>
          <TagsGrid>
            {filteredTags.map((tag) => (
              <InfoItem key={tag.key}>
                <Label>{tag.name.getTranslation(locale)}</Label>
                <Value>{tag.value || "-"}</Value>
              </InfoItem>
            ))}
          </TagsGrid>
        </CollapsibleSection>
      )}

      <SectionDivider
        onClick={hasExpandableContent ? () => setIsExpanded((prev) => !prev) : undefined}
        isExpanded={isExpanded}
      />
    </>
  );
};
